import type { ApiErrorBody, ErrorCode } from '@train2volley/shared';

/** Fout van de API met de stabiele `code` uit docs/05; toon `message` aan de gebruiker. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

const GENERIC = 'Er ging iets mis. Probeer het opnieuw.';

/**
 * fetch naar /api/v1 op dezelfde origin (Apache of de Vite-proxy). De browser
 * stuurt bij schrijvende verzoeken zelf de Origin-header mee (D-023).
 */
export async function api<T>(
  path: string,
  init: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown } = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method: init.method ?? 'GET',
      credentials: 'same-origin',
      headers: init.body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    throw new ApiError(
      0,
      'INTERNAL',
      'Geen verbinding. Controleer je internet en probeer het opnieuw.',
    );
  }
  if (res.status === 204) return undefined as T;

  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const body = (data ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, body.code ?? 'INTERNAL', body.message ?? GENERIC, body.details);
  }
  return data as T;
}

export function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : GENERIC;
}
